import * as React from "react";
import { ChevronsUpDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

const DEFAULT_MAX_DISPLAY = 2;

const SearchableMultiSelect = ({
  options = [],
  value = [],
  onChange,
  placeholder = "Select options...",
  emptyText = "No option found.",
  searchPlaceholder = "Search...",
  disabled = false,
  className,
  maxDisplay = DEFAULT_MAX_DISPLAY,
  maxLabelLength = Infinity,
}) => {
  const [open, setOpen] = React.useState(false);
  const selectableOptions = React.useMemo(
    () => options.filter((option) => option.value !== "All"),
    [options],
  );
  const singleSelectableOption =
    selectableOptions.length === 1 ? selectableOptions[0] : null;
  const resolvedOptions = singleSelectableOption ? selectableOptions : options;
  const isSingleOptionLocked = Boolean(singleSelectableOption);

  const selectedOptions = React.useMemo(
    () => resolvedOptions.filter((option) => value.includes(option.value)),
    [resolvedOptions, value],
  );

  React.useEffect(() => {
    if (!singleSelectableOption) return;

    const singleValue = singleSelectableOption.value;

    if (value.length !== 1 || value[0] !== singleValue) {
      onChange?.([singleValue]);
    }
  }, [onChange, singleSelectableOption, value]);

  const toggleValue = (nextValue) => {
    if (isSingleOptionLocked) return;

    if (value.includes(nextValue)) {
      onChange(value.filter((item) => item !== nextValue));
      return;
    }
    onChange([...value, nextValue]);
  };

  const removeValue = (nextValue, event) => {
    event.preventDefault();
    event.stopPropagation();
    if (isSingleOptionLocked) return;

    onChange(value.filter((item) => item !== nextValue));
  };

  const visibleOptions = selectedOptions.slice(0, maxDisplay);
  const hiddenCount = Math.max(
    selectedOptions.length - visibleOptions.length,
    0,
  );

  const truncateLabel = (text, max = maxLabelLength) => {
    if (!text) return "";

    if (max === Infinity) return text;

    return text.length > max ? text.slice(0, max) + "..." : text;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "h-auto min-h-9 w-full justify-between px-3 py-1 text-left text-base font-normal md:text-sm dark:bg-background/30 bg-transparent",
            className,
          )}
          disabled={disabled}
        >
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
            {selectedOptions.length === 0 ? (
              <span className="truncate text-muted-foreground">
                {placeholder}
              </span>
            ) : (
              <>
                {visibleOptions.map((option) => (
                  <Badge
                    key={option.value}
                    variant="secondary"
                    className="max-w-full gap-1"
                  >
                    <span className="truncate">
                      {truncateLabel(option.label)}
                    </span>
                    {!isSingleOptionLocked ? (
                      <button
                        type="button"
                        onClick={(event) => removeValue(option.value, event)}
                        onMouseDown={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                        }}
                        className="rounded-full"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    ) : null}
                  </Badge>
                ))}
                {hiddenCount > 0 ? (
                  <Badge variant="secondary">+{hiddenCount} more</Badge>
                ) : null}
              </>
            )}
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
      >
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList className="max-h-[300px] overscroll-contain">
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {resolvedOptions.map((option) => {
                const optionValue = option.value ?? "";
                const optionLabel = String(
                  option.label ?? option.value ?? option.key ?? "",
                );
                const optionMeta = option.meta
                  ? String(option.meta)
                  : option.code
                    ? String(option.code)
                    : "";
                const isSelected = value.includes(optionValue);

                return (
                  <CommandItem
                    key={optionValue}
                    value={`${optionLabel} ${optionMeta}`.trim()}
                    onSelect={() => toggleValue(optionValue)}
                    disabled={isSingleOptionLocked}
                  >
                    <Checkbox
                      checked={isSelected}
                      disabled={isSingleOptionLocked}
                      className="mr-2 shrink-0"
                    />
                    <span className="min-w-0 flex-1 truncate">
                      {optionLabel}
                    </span>
                    {/* {optionMeta ? (
                      <span className="ml-3 shrink-0 text-xs font-medium text-muted-foreground">
                        {optionMeta}
                      </span>
                    ) : null} */}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default SearchableMultiSelect;
